import { useState, useEffect, useCallback } from 'react';
import fetchWithAuth from '../fetchWithAuth';
import { toast } from 'react-toastify';

const useDeals = (user, onSessionExpired) => {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchDeals = useCallback(async () => {
    if (!user || !user.token || !user.organizationId) {
      console.log('User or token missing:', user);
      setError('User authentication required');
      setDeals([]);
      return;
    }

    setLoading(true);
    const organizationId = typeof user.organizationId === 'object' ? user.organizationId._id : user.organizationId;
    console.log('Fetching deals for user:', user.username, 'with organizationId:', organizationId);
    try {
      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/members/deals`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'organizationId': organizationId.toString(),
        },
      }, onSessionExpired, 10000);
      const data = await response.json();
      console.log('Fetched deals:', data);
      setDeals(Array.isArray(data) ? data : []);
      localStorage.setItem('dealsCache', JSON.stringify(data));
      setError('');
    } catch (err) {
      if (err.message === 'Session expired') return;
      console.error('Error fetching deals:', {
        message: err.message,
        stack: err.stack
      });
      setError(err.message);
      setDeals(JSON.parse(localStorage.getItem('dealsCache') || '[]'));
      toast.error('Failed to fetch deals');
    } finally {
      setLoading(false);
    }
  }, [user, onSessionExpired]);

  const handleDealRedirect = useCallback(async (deal) => {
    if (!user || !user.token || !deal._id || !deal.link) {
      console.log('Missing data for redirect:', { user, deal });
      return;
    }
    try {
      console.log(`Tracking redirect for deal ${deal._id}`);
      await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/deals/${deal._id}/redirect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`,
        },
      }, onSessionExpired, 10000);
      console.log(`Opening deal link: ${deal.link}`);
      window.open(deal.link, '_blank');
    } catch (err) {
      if (err.message === 'Session expired') return;
      console.error('Failed to track redirect:', {
        message: err.message,
        stack: err.stack
      });
      toast.error('Failed to track deal redirect');
    }
  }, [user, onSessionExpired]);

  const toggleDealStatus = useCallback(async (dealId) => {
    if (!user || !user.token) {
      console.log('Missing user or token for toggling deal status');
      setError('User authentication required');
      return;
    }
    try {
      console.log(`Toggling status for deal ${dealId}`);
      const response = await fetchWithAuth(`${process.env.REACT_APP_API_URL}/api/deals/${dealId}/deactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
      }, onSessionExpired, 10000);
      const updatedDeal = await response.json();
      console.log(`Deal ${dealId} status toggled to ${updatedDeal.deal.active ? 'active' : 'inactive'}`);
      setDeals(deals => {
        const newDeals = deals.map(deal => deal._id === dealId ? updatedDeal.deal : deal);
        localStorage.setItem('dealsCache', JSON.stringify(newDeals));
        return newDeals;
      });
      setError('');
      toast.success(`Deal ${updatedDeal.deal.active ? 'activated' : 'deactivated'}`);
    } catch (err) {
      if (err.message === 'Session expired') return;
      console.error('Error toggling deal status:', {
        message: err.message,
        stack: err.stack
      });
      setError(err.message);
      toast.error('Failed to toggle deal status');
    }
  }, [user, onSessionExpired]);

  useEffect(() => {
    if (user && user.token && user.organizationId) {
      fetchDeals();
    }
  }, [user, fetchDeals]);

  return { deals, fetchDeals, handleDealRedirect, toggleDealStatus, loading, error };
};

export default useDeals;